// Signalarten (Issue #352): Farbe und Gruppierung der Katalog-Variablen.
//
// `direction` sagt nur, in welche Richtung ein Signal läuft. `kind` sagt, WAS es ist — und das ist
// der Unterschied, der beim Zeichnen zählt: ein exogenes Signal (Wetter, Strompreis, Kalender) ist
// keine Rückkopplung der Anlage, sondern eine Randbedingung aus der Zukunft, auf die geplant wird.
// Wer das im Bild nicht unterscheiden kann, verdrahtet es früher oder später wie einen Messwert.
//
// Ein Zustandssignal (`health`) wiederum gehört in die Instandhaltung und ausdrücklich NICHT in ein
// Vorhersagemodell — auch das soll man sehen, bevor man es an einen Modellknoten hängt.

import type { CatalogVariable, SignalKind } from '../api/types';

// Fehlt `kind`, gilt die Vorgabe aus `direction` — genau wie im Backend (kind_of).
export function kindOf(variable: Pick<CatalogVariable, 'direction' | 'kind'>): SignalKind {
  if (variable.kind) return variable.kind;
  return variable.direction === 'output' ? 'output' : 'input';
}

// Rahmenfarbe je Signalart. Die Zuordnung folgt der Bedeutung, nicht dem Geschmack:
// Messwerte grün (kommt herein) und Stellwerte blau (geht hinaus) sind der Bestand; exogene
// Größen bekommen Violett, weil sie als einzige nicht aus der Anlage stammen, und
// Zustandssignale Bernstein — dieselbe Warnfarbe, die die Ansicht schon für „hier stimmt etwas
// nicht" verwendet.
const KIND_ACCENT: Record<SignalKind, string> = {
  input: 'border-emerald-500/60',
  output: 'border-sky-500/60',
  setpoint: 'border-indigo-500/60',
  disturbance: 'border-orange-500/60',
  exogenous: 'border-violet-500/60',
  health: 'border-amber-500/60',
};

export function accentForKind(kind: SignalKind): string {
  return KIND_ACCENT[kind] ?? 'border-bf-border';
}

// Reihenfolge der Gruppen in den Tag-Auswahlfeldern. Messwerte zuerst (der häufigste Fall),
// exogene Größen und Zustandssignale danach als eigene Blöcke — in einer gemeinsamen Liste
// gingen sie zwischen hunderten Anlagen-Tags unter, und genau das soll nicht passieren.
export const KIND_ORDER: SignalKind[] = [
  'input',
  'disturbance',
  'exogenous',
  'health',
  'output',
  'setpoint',
];

// Gruppiert Variablen nach Signalart, in der Reihenfolge oben; leere Gruppen fallen weg.
export function groupByKind(
  variables: CatalogVariable[],
): { kind: SignalKind; variables: CatalogVariable[] }[] {
  const buckets = new Map<SignalKind, CatalogVariable[]>();
  for (const v of variables) {
    const k = kindOf(v);
    const list = buckets.get(k) ?? [];
    list.push(v);
    buckets.set(k, list);
  }
  return KIND_ORDER.filter((k) => (buckets.get(k)?.length ?? 0) > 0).map((k) => ({
    kind: k,
    variables: buckets.get(k) as CatalogVariable[],
  }));
}
